import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  apiErrorText,
  useGetPtoRdChangeReconcileQuery,
  useGetPtoVolumeHistoryQuery,
  useListPtoVolumeEditsQuery,
  useListPtoVolumesQuery,
  useResolvePtoVolumeEditMutation,
  useRunPtoRdChangeReconcileMutation,
  useWithdrawPtoVolumeEditMutation,
  type PtoVolumeEdit,
} from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import { rowLabel } from '../volumes/rowRef';
import {
  changeDocDecisionGroups,
  dateRu,
  engineerEdit,
  isChangeDocRow,
  qtyText,
  statusLabel,
  valuesText,
} from './logic';
import { useRdVolumeEditAccess, type RdVolumeEditAccess } from './useRdVolumeEditAccess';
import { Buttons, ErrorText, Muted, ReasonDialog } from './ui';

const Panel = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const Title = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
`;

const Group = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: ${({ theme }) => theme.spacing(3)};
`;

const GroupHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  font-weight: 600;
`;

const Item = styled.div`
  padding: ${({ theme }) => theme.spacing(2.5)} 0;
  border-bottom: 1px dashed ${({ theme }) => theme.colors.border};
  font-size: 13px;
  line-height: 1.5;
`;

const Strong = styled.span`
  font-weight: 600;
`;

/** Строка действующей ревизии, в которой появился материал: кандидаты сверки
 *  или поиск по позициям ревизии. */
function TargetPicker({
  edit,
  activeRevisionId,
  onPick,
  busy,
}: {
  edit: PtoVolumeEdit;
  activeRevisionId: string | null;
  onPick: (positionId: string) => void;
  busy: boolean;
}) {
  // Кандидаты сверки — в `raw.engineer_edit.candidates` позиции (Б).
  const { data: history } = useGetPtoVolumeHistoryQuery(edit.position_id ?? '', {
    skip: !edit.position_id,
  });
  const candidates = useMemo(
    () => engineerEdit(history?.position.raw)?.candidates ?? [],
    [history],
  );
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const { data: volumes } = useListPtoVolumesQuery(activeRevisionId ?? '', {
    skip: !searching || !activeRevisionId,
  });
  const found = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!volumes || q.length < 2) return [];
    return volumes.items
      .filter(
        (p) => !isChangeDocRow(p) && p.decision !== 'rejected' && p.name.toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [volumes, search]);
  const [target, setTarget] = useState('');

  return (
    <div>
      {candidates.length > 0 && (
        <Select
          aria-label="Строка новой ревизии"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          style={{ height: 32, fontSize: 12.5, marginBottom: 6 }}
        >
          <option value="">— строка новой ревизии (кандидаты сверки) —</option>
          {candidates.map((c) => (
            <option key={c.positionId} value={c.positionId}>
              {c.label ? `${c.label} — ` : ''}
              {c.name.slice(0, 70)} · {qtyText(c.quantity, c.unit)}
            </option>
          ))}
        </Select>
      )}
      {!searching && activeRevisionId && (
        <Button $variant="ghost" $size="sm" onClick={() => setSearching(true)}>
          Найти строку в ревизии…
        </Button>
      )}
      {searching && (
        <>
          <Input
            aria-label="Поиск строки по наименованию"
            placeholder="Наименование строки (от 2 букв)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ height: 32, fontSize: 12.5, marginBottom: 6 }}
          />
          {found.length > 0 && (
            <Select
              aria-label="Найденные строки ревизии"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              style={{ height: 32, fontSize: 12.5, marginBottom: 6 }}
            >
              <option value="">— найдено строк: {found.length} —</option>
              {found.map((p) => (
                <option key={p.id} value={p.id}>
                  {rowLabel(p) ?? `стр. ${p.page_number}`} — {p.name.slice(0, 70)} ·{' '}
                  {qtyText(p.quantity, p.unit)}
                </option>
              ))}
            </Select>
          )}
        </>
      )}
      <Button
        $variant="secondary"
        $size="sm"
        disabled={!target || busy}
        onClick={() => onPick(target)}
      >
        Появилось в ревизии — закрыть позицию
      </Button>
    </div>
  );
}

function ChangeItem({
  edit,
  rights,
  activeRevisionId,
  projectId,
}: {
  edit: PtoVolumeEdit;
  rights: RdVolumeEditAccess;
  activeRevisionId: string | null;
  projectId: string;
}) {
  const [resolve, { isLoading: resolving }] = useResolvePtoVolumeEditMutation();
  const [withdraw, { isLoading: withdrawing }] = useWithdrawPtoVolumeEditMutation();
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(action: 'appeared' | 'not_in_revision', positionId?: string) {
    setError(null);
    try {
      await resolve({ id: edit.id, body: { action, position_id: positionId } }).unwrap();
    } catch (e) {
      setError(apiErrorText(e, 'Решение не сохранено.'));
    }
  }

  return (
    <Item>
      <div>
        <Strong>{valuesText(edit.value)}</Strong> · ревизия {edit.revision_no ?? '—'} ·{' '}
        <Badge $tone="warning">{statusLabel(edit.status)}</Badge>
      </div>
      <Muted>
        Внёс {edit.created_by_name ?? '—'}, {dateRu(edit.created_at)}
        {edit.urgency ? ` · обоснование: ${edit.urgency}` : ''}
      </Muted>
      {edit.status === 'not_in_revision' && (
        <Muted>
          Материала нет в новой ревизии: позиция вне сумм — действует ревизия. Отзовите её с
          причиной или внесите заново по документу, согласованному Заказчиком, с новым обоснованием.
        </Muted>
      )}
      {edit.status === 'needs_decision' && (
        <Muted>
          В новой ревизии другое количество или несколько похожих строк — решает инженер; в суммах
          действует ревизия.
        </Muted>
      )}
      {rights.canEdit && (
        <>
          <Buttons style={{ marginTop: 6 }}>
            <Button $variant="ghost" $size="sm" onClick={() => setOpen((x) => !x)}>
              {open ? 'Свернуть' : 'Появилось в ревизии…'}
            </Button>
            {edit.status === 'needs_decision' && (
              <Button
                $variant="ghost"
                $size="sm"
                disabled={resolving}
                onClick={() => void decide('not_in_revision')}
              >
                Нет в ревизии
              </Button>
            )}
            <Button $variant="ghost" $size="sm" onClick={() => setAsking(true)}>
              Отозвать
            </Button>
            <Link to={`/agents/pto/${projectId}/changes?tab=docs`}>Внести заново по документу</Link>
          </Buttons>
          {open && (
            <div style={{ marginTop: 6 }}>
              <TargetPicker
                edit={edit}
                activeRevisionId={activeRevisionId}
                busy={resolving}
                onPick={(id) => void decide('appeared', id)}
              />
            </div>
          )}
        </>
      )}
      {error && <ErrorText>{error}</ErrorText>}
      {asking && (
        <ReasonDialog
          title="Отозвать позицию по документу изменения"
          warning="Позиция останется в истории и журнале; в суммах её нет."
          confirmLabel="Отозвать"
          busy={withdrawing}
          error={error}
          onClose={() => setAsking(false)}
          onConfirm={(reason) => {
            setError(null);
            withdraw({ id: edit.id, reason })
              .unwrap()
              .then(() => setAsking(false))
              .catch((e: unknown) => setError(apiErrorText(e, 'Позиция не отозвана.')));
          }}
        />
      )}
    </Item>
  );
}

function DocumentGroup({
  documentId,
  code,
  items,
  rights,
  projectId,
}: {
  documentId: string;
  code: string;
  items: PtoVolumeEdit[];
  rights: RdVolumeEditAccess;
  projectId: string;
}) {
  const { data: view } = useGetPtoRdChangeReconcileQuery(documentId);
  const [run, { isLoading }] = useRunPtoRdChangeReconcileMutation();
  const [note, setNote] = useState<string | null>(null);

  async function onRun() {
    setNote(null);
    try {
      const r = await run(documentId).unwrap();
      setNote(
        `Сверено с ревизией ${r.revision_no ?? '—'}: позиций ${r.checked}, закрыто ${r.confirmed}, нет в ревизии ${r.not_in_revision}, спорно ${r.needs_decision}.`,
      );
    } catch (e) {
      setNote(apiErrorText(e, 'Сверка не выполнена.'));
    }
  }

  return (
    <Group>
      <GroupHead>
        <span>
          {code || 'Комплект РД'}
          {view?.active_revision_no ? ` · действующая ревизия ${view.active_revision_no}` : ''}
        </span>
        {rights.canEdit && (
          <Button $variant="secondary" $size="sm" disabled={isLoading} onClick={() => void onRun()}>
            {isLoading ? 'Сверка…' : 'Пересверить с ревизией'}
          </Button>
        )}
      </GroupHead>
      {view && (
        <Muted>
          Нет в ревизии: {view.summary.not_in_revision} · спорно: {view.summary.needs_decision} ·
          закрыто ревизией: {view.summary.confirmed_by_revision} · отозвано:{' '}
          {view.summary.withdrawn}
        </Muted>
      )}
      {note && <Muted>{note}</Muted>}
      {items.map((e) => (
        <ChangeItem
          key={e.id}
          edit={e}
          rights={rights}
          activeRevisionId={view?.active_revision_id ?? null}
          projectId={projectId}
        />
      ))}
    </Group>
  );
}

/**
 * «Новая ревизия РД» — позиции по документам изменения (№8, Б) прежних ревизий,
 * ждущие решения: «нет в ревизии N» (вне сумм — действует ревизия; «Инженер+»
 * отзывает или вносит заново по документу) и «спорно» (другое количество,
 * неоднозначное совпадение — решает инженер). Материал, который программа нашла
 * в ревизии, закрывается сам, без двойного счёта. Нечего решать — блока нет.
 */
export function ChangeDocCarryOver({ projectId }: { projectId: string }) {
  const rights = useRdVolumeEditAccess(projectId);
  const { data } = useListPtoVolumeEditsQuery(
    { projectId, kind: 'change_doc', status: 'not_in_revision,needs_decision' },
    { skip: !rights.enabled || !projectId },
  );
  const groups = useMemo(() => changeDocDecisionGroups(data?.items ?? []), [data]);
  if (!rights.enabled || groups.length === 0) return null;
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  return (
    <Panel aria-label="Позиции по документам изменения при новой ревизии">
      <Title>Новая ревизия РД: позиции по документам изменения ({total})</Title>
      <Muted>
        Выдана новая ревизия комплекта — вносить материал по документу для неё нельзя. Что появилось
        в ревизии, закрывается без двойного счёта; чего нет — вне сумм, на проверку.
      </Muted>
      {groups.map((g) => (
        <DocumentGroup
          key={g.documentId}
          documentId={g.documentId}
          code={g.code}
          items={g.items}
          rights={rights}
          projectId={projectId}
        />
      ))}
    </Panel>
  );
}
